import styles from './BundleSummaryModule.module.scss';
import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { useEffect, useRef, useState, FC, HTMLAttributes } from 'react';
import {
  CardsArrangement,
  Bundle,
  BundleSummaryModule as BundleSummaryModuleType,
} from 'api/contentful/contentTypes.cloverdotcom';
import { Grid } from 'components/Grid/Grid';
import { GridContainer } from 'components/GridContainer/GridContainer';
import { TextV2 } from 'components/TextV2/TextV2';
import { useABSolution } from 'hooks/useABSolution';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { flattenContentfulData, FlatEntry } from 'services/cms/flattenContentful';
import { generateId } from 'utils/IdGeneration';
import { isFirefox } from 'utils/UserAgent';
import { ScreenReaderBundleFeatures } from '../BundleSummaryFeatures/ScreenReaderBundleFeatures';
import { BundleSummaryMoreContainer } from '../BundleSummaryMoreContainer/BundleSummaryMoreContainer';
import { ButtonLinkV2 } from '../Button/ButtonLinkV2';
import { BundleCell } from './BundleCell';
import { BUNDLE_SUMMARY_MODULE_TEST_ID } from './BundleSummaryModule.const';
import { TextWithPaymentsComparisonLink } from 'components/PaymentsComparisonLink/utils/TextWithPaymentsComparisonLink';

const containerMaxWidth = 1336;

interface IBundleSummaryModuleProps extends HTMLAttributes<HTMLDivElement> {
  entry?: FlatEntry<BundleSummaryModuleType>;
  bundles?: Bundle[];
}

const BundleSummaryModule: FC<IBundleSummaryModuleProps> = ({ entry, className, ...props }) => {
  const [showAllCards, setShowMoreFeatures] = useState(false);
  const showMoreContainerRef = useRef<HTMLDivElement>(null);
  const bundleSummarySectionRef = useRef<HTMLDivElement>(null);

  const toggleShowMoreFeatures = () => {
    const showMoreContainerTop = showMoreContainerRef?.current?.getBoundingClientRect()?.top ?? 0;
    const setBundleSummarySectionProperty = (prop = '', val = '') => {
      bundleSummarySectionRef?.current?.style.setProperty(prop, val);
    };
    const showMoreContainerTopThreshold = 120;
    // fixes a transition animation issue for Firefox (YMLR-415)
    if (showMoreContainerTop < showMoreContainerTopThreshold && isFirefox) {
      setBundleSummarySectionProperty('--bundle-summary-card-transition', 'opacity .3s ease-in-out');
    } else {
      setBundleSummarySectionProperty(
        '--bundle-summary-card-transition',
        'opacity .3s ease-in-out, min-height .3s ease-in-out',
      );
    }
    setShowMoreFeatures(bool => !bool);
  };

  const { browser } = useBreakpoints();
  const isDesktopOrLarge = browser.is.desktop;

  const {
    cardsArrangement,
    link,
    solution: solutionEntry,
    title,
    titleColor,
    subTitle,
    subTitleColor,
    showMoreCTA,
    showLessCTA,
    isPlusIconForShowMore,
    isBundleFeaturesAlwaysOpen,
    headingTagName = 'h2',
  } = entry || {};
  const solution = useABSolution(solutionEntry);
  const { bundleWithTag, bundles } = solution || {};

  const isWithSpaceLayout = cardsArrangement === CardsArrangement.WITH_SPACE;
  const bundleFeaturesId = `bundle-features=${solution?.id}`;
  const atLeastOneBundleHasTag = (bundles || []).some(({ slug }) => slug === bundleWithTag?.slug);
  const hasTagMargin = isDesktopOrLarge && atLeastOneBundleHasTag;

  const TitleContainer = () => (
    <GridContainer gutter justifyContent="center">
      <Grid item xs={12} md={9} lg={10}>
        {title && (
          <TextV2
            isMarkdown
            value={title}
            entry={{ ...entry, field: 'title' }}
            tagName={headingTagName}
            variant="h2"
            className={styles.bundleSummaryModuleTitle}
            style={{ color: titleColor }}
            id={generateId(title)}
            data-testid={BUNDLE_SUMMARY_MODULE_TEST_ID.bundleSummaryHeading}
          />
        )}
        {subTitle && (
          <div className={styles.bundleSummaryModuleSubTitle}>
            <TextWithPaymentsComparisonLink text={subTitle} textColor={subTitleColor} />
          </div>
        )}
      </Grid>
    </GridContainer>
  );

  useEffect(() => {
    if (isDesktopOrLarge || isWithSpaceLayout) return;
    setShowMoreFeatures(false);
  }, [isDesktopOrLarge, isWithSpaceLayout]);

  if (!entry) return null;

  return (
    <section
      ref={bundleSummarySectionRef}
      className={cx(styles.bundleSummaryModule, className)}
      style={getStyle(entry)}
      {...props}
    >
      {title && <TitleContainer />}
      <GridContainer
        justifyContent="center"
        maxWidth={containerMaxWidth}
        className={cx({ [styles.bundleSummaryModuleCellsWithTagMargin]: hasTagMargin })}
      >
        <Grid item xs>
          <BundleCell entry={solution} {...{ isWithSpaceLayout, showAllCards, ...entry }} />
          {isDesktopOrLarge && !isWithSpaceLayout && !isBundleFeaturesAlwaysOpen && (
            <>
              <div ref={showMoreContainerRef} className={styles.bundleSummaryModuleShowMoreContainer}>
                <BundleSummaryMoreContainer
                  aria-controls={bundleFeaturesId}
                  toggleMore={toggleShowMoreFeatures}
                  showMoreFeatures={showAllCards}
                  showMoreCTA={showMoreCTA}
                  showLessCTA={showLessCTA}
                  isWithSpaceLayout={isWithSpaceLayout}
                  isPlusIconForShowMore={isPlusIconForShowMore}
                />
              </div>
              <ScreenReaderBundleFeatures
                id={bundleFeaturesId}
                aria-hidden={!showAllCards}
                bundles={solution?.bundles}
              />
            </>
          )}
        </Grid>
      </GridContainer>
      <GridContainer gutter={isWithSpaceLayout} justifyContent="center" maxWidth={containerMaxWidth}>
        <Grid item xs={12} md={9} lg={4}>
          <ButtonLinkV2 entry={link} isFullWidth className={styles.bundleSummaryModuleLink} size="small" />
        </Grid>
      </GridContainer>
    </section>
  );
};

interface IBundleSummaryModuleWrapperProps extends HTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<BundleSummaryModuleType>;
}

const BundleSummaryModuleWrapper: FC<IBundleSummaryModuleWrapperProps> = ({ entry, ...props }) => (
  <BundleSummaryModule {...props} entry={flattenContentfulData(entry)} />
);

BundleSummaryModuleWrapper.displayName = 'BundleSummaryModuleWrapper';

export { BundleSummaryModule as _BundleSummaryModule, BundleSummaryModuleWrapper as BundleSummaryModule };

const getStyle = ({
  backgroundColor,
  showCTAFontSize,
  bundleTitleFontSize,
  bundleTitleFontColor,
  bundlePricingTitleFontSize,
  bundlePricingTitleFontColor,
  bundlePricingSubTitleFontSize,
  bundlePricingSubTitleFontColor,
  bundleDescriptionFontSize,
  bundleDescriptionFontColor,
}: FlatEntry<BundleSummaryModuleType>) =>
  ({
    '--background-color': backgroundColor,
    '--show-cta-font-size': showCTAFontSize,
    '--bundle-title-font-size': bundleTitleFontSize,
    '--bundle-title-font-color': bundleTitleFontColor,
    '--bundle-pricing-title-font-size': bundlePricingTitleFontSize,
    '--bundle-pricing-title-font-color': bundlePricingTitleFontColor,
    '--bundle-pricing-subtitle-font-size': bundlePricingSubTitleFontSize,
    '--bundle-pricing-subtitle-font-color': bundlePricingSubTitleFontColor,
    '--bundle-description-font-size': bundleDescriptionFontSize,
    '--bundle-description-font-color': bundleDescriptionFontColor,
  } as React.CSSProperties);
